import {
  batchMovePersonToOtherRecord,
  getNestedOrFloatingElementById,
  getNestedSubelementsById,
  getNextArkId,
} from '@fs/zion-sls'

/**
 * Retrieves a list of persons objects from their IDs.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.images - The array of images containing person data.
 * @param {Array} params.personsIds - The array of person IDs to retrieve.
 * @returns {Array} The list of persons corresponding to the given IDs.
 */
export const getPersonsFromIds = ({ images, personsIds }) =>
  personsIds.map((id) => getNestedOrFloatingElementById(images, id)).filter(Boolean)

/**
 * Generates an array of dispatch actions to save persons data to a record.
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.recordIdParam - The ID of the record or 'new_record' to create a new one.
 * @param {Object|Object[]} params.personsData - The data of the person or an array of persons.
 * @param {string} params.currentPartitionKey - The current partition key.
 * @param {Object[]} params.images - The array of images.
 * @param {string} params.recordType - The type of the record.
 * @returns {Promise<Object[]>} A promise that resolves to an array of dispatch actions.
 */
export const generateSaveDispatches = async ({
  recordIdParam,
  personsData,
  currentPartitionKey,
  images,
  recordType,
}) => {
  let recordId = recordIdParam
  let createNewRecordDispatches = []

  // If it's a single person, put it into an array
  const persons = Array.isArray(personsData) ? personsData : [personsData]

  // We only need to create the new record once if the recordIdParam is 'new_record'
  if (recordIdParam === 'new_record') {
    recordId = await getNextArkId('record')
    createNewRecordDispatches = [
      {
        type: 'CREATE_ELEMENT',
        value: {
          elementType: 'RECORD',
          partitionKey: currentPartitionKey,
          id: recordId,
          extraArgs: recordType !== '' ? { recordType } : {},
        },
      },
      {
        type: 'ADD_SUB',
        value: {
          superPartitionKey: currentPartitionKey,
          subPartitionKey: currentPartitionKey,
          superId: currentPartitionKey,
          subId: recordId,
          elementType: 'IMAGE',
        },
      },
    ]
  }

  // Use reduce to accumulate all dispatches for all persons
  const dispatches = persons.reduce(
    (acc, person) => {
      const sourceRecord = getNestedSubelementsById(images, person?.recordId)?.[0]
      const destinationRecord = getNestedSubelementsById(images, recordId)?.[0]

      // Add move dispatch for the person
      const personMoveDispatches = batchMovePersonToOtherRecord({
        person,
        destRecordId: recordId,
        sourceRecPartitionKey: sourceRecord?.partitionKey,
        destRecPartitionKey: destinationRecord ? destinationRecord.partitionKey : currentPartitionKey,
      })

      // Add the dispatches for this person to the accumulator
      acc.push(...personMoveDispatches)

      return acc
    },
    [...createNewRecordDispatches]
  )

  return dispatches
}

/**
 * Handles the drag start event for a person element.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.active - The active draggable element.
 * @param {Object} params.formMethods - The form methods for handling form state.
 * @param {Array} params.images - The array of zion Sls image objects.
 * @param {Array} params.allRecords - The array of all record objects.
 */
export const onDragStartHandler = ({ active, formMethods, images, allRecords }) => {
  const [selectedPersonsIds, isMultiSelect] = formMethods.getValues(['selectedPersonsIds', 'isMultiSelect'])
  const draggedElementId = active?.id
  const isDraggedElementSelected = selectedPersonsIds.includes(draggedElementId)

  if (!isDraggedElementSelected) {
    formMethods.setValue('selectedPersonsIds', [draggedElementId, ...selectedPersonsIds])
  }

  const dragPerson = getNestedOrFloatingElementById(images, draggedElementId)
  const newDragInfo = {
    isDragging: true,
    person: dragPerson,
    record: getNestedSubelementsById(allRecords, dragPerson?.recordId)?.[0],
  }
  formMethods.setValue('dragInfo', newDragInfo)

  if (!isMultiSelect) formMethods.setValue('selectedPersonsIds', [dragPerson?.id])
}

/**
 * Handles the drag end event for dragging persons between records.
 *
 * @param {Object} params - The parameters for the handler.
 * @param {Object} params.active - The active draggable item.
 * @param {Object} params.over - The droppable area where the item is dropped.
 * @param {Object} params.formMethods - Methods for handling form state.
 * @param {Array} params.images - The list of images.
 * @param {Object} params.newRecordOverlay - The overlay for creating a new record. From the useOverlay hook
 * @param {string} params.currentImageArk - The ARK identifier for the current image.
 * @param {Function} params.savePersonInOtherRecord - Function to save persons in another record.
 */
export const onDragEndHandler = ({
  active,
  over,
  formMethods,
  images,
  newRecordOverlay,
  currentImageArk,
  savePersonInOtherRecord,
}) => {
  const selectedPersonsIds = formMethods.getValues('selectedPersonsIds')
  const draggedPersons = getPersonsFromIds({ images, personsIds: selectedPersonsIds })

  const activeRecordId = active?.data?.current?.record?.id
  const destinationRecordId = over?.id
  const allSelectedPersonsFromSameRecord = draggedPersons.every((person) => person?.recordId === activeRecordId)

  // If the selected persons are from the same record and the destination is the same record, do nothing
  if (allSelectedPersonsFromSameRecord && activeRecordId === destinationRecordId) {
    formMethods.setValue('dragInfo', null)
    formMethods.setValue('selectedPersonsIds', [])
    return
  }

  // If the destination is a new record, open the new record dialog
  if (destinationRecordId === 'new_record') {
    newRecordOverlay.handleClick()
  } else {
    // If the destination is an existing record, save the persons in the destination record
    savePersonInOtherRecord(destinationRecordId, draggedPersons, currentImageArk)
  }

  formMethods.setValue('dragInfo', null)
}

/**
 * Handles the drag cancel event by resetting the drag information in the form.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.formMethods - The form methods object.
 */
export const onDragCancelHandler = ({ formMethods }) => {
  formMethods.setValue('dragInfo', null)
}
